import { useEffect, useMemo } from 'react';
import {
  ObjectiveFilterManager,
  useValidateManagerObjectiveWeightsQuery,
  useValidateObjectiveWeightsQuery,
} from '../../../__generated__/graphql';
import { useGoalsContext } from './GoalsContext/GoalsContext';
import { useUserInfo } from '../../../context/UserInfo';
import { useToasts } from '../../../../../context/Toast';
import { GoalFilter } from './GoalsFilter/GoalsFilter';
import { NULL_CATEGORY_ID } from './types';

export type EmployeeValidationErrors = {
  employeeId: string;
  employeeName: string;
  errors: readonly { errorCode: string; errorMessage: string }[];
};

export type GoalWeightValidation = {
  valid: boolean;
  invalidCategoryIds: Set<string>;
  errors: readonly { errorCode: string; errorMessage: string }[];
  errorsByCategoryId: Map<string, string[]>;
  employeeValidations: readonly EmployeeValidationErrors[];
  invalidEmployeeCount: number;
  getErrorsForEmployee: (
    employeeId: string,
  ) => readonly { errorCode: string; errorMessage: string }[];
};

export const deriveManagerValidationExtras = (
  employeeValidations: readonly EmployeeValidationErrors[],
) => {
  const errorsByEmployeeId = new Map(
    employeeValidations.map((ev) => [ev.employeeId, ev.errors] as const),
  );
  return {
    invalidEmployeeCount: employeeValidations.length,
    getErrorsForEmployee: (employeeId: string) =>
      errorsByEmployeeId.get(employeeId) ?? [],
  };
};

const emptyValidation: GoalWeightValidation = {
  valid: true,
  invalidCategoryIds: new Set(),
  errors: [],
  errorsByCategoryId: new Map(),
  employeeValidations: [],
  ...deriveManagerValidationExtras([]),
};

const toManagerFilter = (
  filter: GoalFilter,
  developmentFrameworkEnabled: boolean,
  currentPeriodId: string | null,
): ObjectiveFilterManager => ({
  tagId: filter.tagId,
  periodId:
    developmentFrameworkEnabled && currentPeriodId
      ? currentPeriodId
      : filter.periodId,
  employeeId: filter.employeeId,
  locationId: filter.locationId,
  goalType: filter.goalType,
  categoryId:
    filter.categoryId === null
      ? null
      : filter.categoryId === NULL_CATEGORY_ID
      ? { value: null }
      : { value: filter.categoryId },
  isRoot: null,
  approvalStatus: null,
});

export const useGoalWeightValidation = (
  filter?: GoalFilter,
): GoalWeightValidation => {
  const { currentPeriodId, isManagerView, developmentFrameworkEnabled } =
    useGoalsContext();
  const { id: employeeId } = useUserInfo();
  const { addError } = useToasts();

  const { data: employeeData, error: employeeError } =
    useValidateObjectiveWeightsQuery({
      variables: {
        objectivePeriodId: currentPeriodId ?? '',
        employeeIds: [employeeId],
      },
      skip: !currentPeriodId || isManagerView,
    });

  const { data: managerData, error: managerError } =
    useValidateManagerObjectiveWeightsQuery({
      variables: {
        objectivePeriodId: currentPeriodId ?? '',
        filter: filter
          ? toManagerFilter(
              filter,
              developmentFrameworkEnabled,
              currentPeriodId,
            )
          : null,
      },
      skip: !currentPeriodId || !isManagerView,
    });

  const error = employeeError || managerError;

  useEffect(() => {
    if (error) {
      addError(error.message);
    }
  }, [error, addError]);

  return useMemo(() => {
    if (isManagerView) {
      const results = managerData?.manager?.objectiveWeightValidationErrors;
      if (!results) {
        return emptyValidation;
      }
      const hasInvalid = results.some((r) => !r.valid);
      if (!hasInvalid) {
        return emptyValidation;
      }
      const employeeValidations = results
        .filter((r) => !r.valid)
        .map((r) => ({
          employeeId: r.employeeId,
          employeeName: r.employee.Name,
          errors: r.errors,
        }));
      return {
        valid: false,
        invalidCategoryIds: new Set<string>(),
        errors: [],
        errorsByCategoryId: new Map<string, string[]>(),
        employeeValidations,
        ...deriveManagerValidationExtras(employeeValidations),
      };
    }

    if (!employeeData) {
      return emptyValidation;
    }

    const result = employeeData.objectiveWeightValidationErrors.find(
      (r) => r.employeeId === employeeId,
    );

    if (!result || result.valid) {
      return emptyValidation;
    }

    const invalidCategoryIds = new Set(
      result.errors.flatMap((e) => e.categoryIds),
    );

    const errorsByCategoryId = new Map<string, string[]>();
    for (const err of result.errors) {
      for (const categoryId of err.categoryIds) {
        const existing = errorsByCategoryId.get(categoryId);
        if (existing) {
          existing.push(err.errorMessage);
        } else {
          errorsByCategoryId.set(categoryId, [err.errorMessage]);
        }
      }
    }

    return {
      valid: false,
      invalidCategoryIds,
      errors: result.errors,
      errorsByCategoryId,
      employeeValidations: [],
      ...deriveManagerValidationExtras([]),
    };
  }, [isManagerView, managerData, employeeData, employeeId]);
};
